// hooks/useMaintenanceManager.ts
import { useState, useEffect, useCallback } from 'react';
import { supabaseService } from '../SupabaseService';

interface MaintenanceStatus {
  enabled: boolean;
  message?: string;
  start_time?: string;
  estimated_end?: string;
  set_by?: string;
}

interface MaintenanceManagerReturn {
  maintenanceStatus: MaintenanceStatus;
  isMaintenanceModeActive: boolean;
  startMaintenance: (message: string, estimatedMinutes: number) => Promise<boolean>;
  endMaintenance: () => Promise<boolean>;
  sendWarning: (message: string, minutesUntil: number) => Promise<boolean>;
  getActiveUsers: () => Promise<any[]>;
  checkMaintenanceStatus: () => Promise<void>;
}

export const useMaintenanceManager = (
  showInfo: (message: string, title?: string) => void,
  showWarning: (message: string, title?: string) => void,
  showError: (message: string, title?: string) => void
): MaintenanceManagerReturn => {
  const [maintenanceStatus, setMaintenanceStatus] = useState<MaintenanceStatus>({ enabled: false });
  const [isMaintenanceModeActive, setIsMaintenanceModeActive] = useState(false);

  // Sjekk vedlikeholdsstatus
  const checkMaintenanceStatus = useCallback(async () => {
    try {
      const status = await supabaseService.getMaintenanceStatus();
      setMaintenanceStatus(status);
      setIsMaintenanceModeActive(status.enabled);
    } catch (error) {
      console.error('Feil ved henting av vedlikeholdsstatus:', error);
    }
  }, []);

  // Start vedlikehold
  const startMaintenance = useCallback(async (message: string, estimatedMinutes: number): Promise<boolean> => {
    try {
      const success = await supabaseService.setMaintenanceMode(true, message, estimatedMinutes);
      if (success) {
        await checkMaintenanceStatus();
        showInfo(`Vedlikeholdsmodus aktivert for ${estimatedMinutes} minutter`, "Vedlikehold");
        return true;
      } else {
        showError("Kunne ikke aktivere vedlikeholdsmodus", "Feil");
        return false;
      }
    } catch (error) {
      console.error('Feil ved start av vedlikehold:', error);
      showError("Feil ved start av vedlikehold", "Feil");
      return false;
    }
  }, [checkMaintenanceStatus, showInfo, showError]);

  // Avslutt vedlikehold
  const endMaintenance = useCallback(async (): Promise<boolean> => {
    try {
      const success = await supabaseService.setMaintenanceMode(false);
      if (success) {
        await checkMaintenanceStatus();
        showInfo("Vedlikeholdsmodus avsluttet", "Vedlikehold");
        return true;
      } else {
        showError("Kunne ikke avslutte vedlikeholdsmodus", "Feil");
        return false;
      }
    } catch (error) {
      console.error('Feil ved avslutning av vedlikehold:', error);
      showError("Feil ved avslutning av vedlikehold", "Feil");
      return false;
    }
  }, [checkMaintenanceStatus, showInfo, showError]);

  // Send varsel
  const sendWarning = useCallback(async (message: string, minutesUntil: number): Promise<boolean> => {
    try {
      const success = await supabaseService.sendMaintenanceWarning(message, minutesUntil);
      if (success) {
        showInfo(`Varsel sendt til alle brukere - vedlikehold om ${minutesUntil} min`, "Varsel sendt");
        return true;
      } else {
        showError("Kunne ikke sende varsel", "Feil");
        return false;
      }
    } catch (error) {
      console.error('Feil ved sending av varsel:', error);
      showError("Feil ved sending av varsel", "Feil");
      return false;
    }
  }, [showInfo, showError]);

  // Hent aktive brukere
  const getActiveUsers = useCallback(async (): Promise<any[]> => {
    try {
      return await supabaseService.getActiveUsers();
    } catch (error) {
      console.error('Feil ved henting av aktive brukere:', error);
      return [];
    }
  }, []);

  // Sjekk status regelmessig
  useEffect(() => {
    checkMaintenanceStatus();
    const interval = setInterval(checkMaintenanceStatus, 30000); // Hver 30 sekund
    return () => clearInterval(interval);
  }, [checkMaintenanceStatus]);

  return {
    maintenanceStatus,
    isMaintenanceModeActive,
    startMaintenance,
    endMaintenance,
    sendWarning,
    getActiveUsers,
    checkMaintenanceStatus,
  };
};